import { useTranslation } from '@jq4race/i18n';
import { cn } from '@jq4race/ui';
import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const DEBOUNCE_MS = 300;

export function SearchInput({ value, onChange, placeholder }: SearchInputProps) {
  const { t } = useTranslation('search');
  const [draft, setDraft] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync draft when an external reset clears the controlled value
  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(draft);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [draft, onChange]);

  return (
    <div className="relative flex items-center">
      <Search
        className="pointer-events-none absolute left-3 size-4 text-muted-foreground"
        aria-hidden
      />
      <input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder ?? t('searchInputPlaceholder')}
        aria-label={t('searchInputAria')}
        className={cn(
          'w-full rounded-sm border border-border bg-card px-4 py-3 pl-9',
          'font-sans text-sm text-foreground placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring',
        )}
      />
    </div>
  );
}
